import React, { useEffect, useState } from 'react';
import ActionIconButton from './ActionIconButton';
import { useApp } from '../context/AppContext';
import { apiClient } from '../utils/apiClient';
import './management-forms.css';

const catalogs = {
  reservationTypes: { title: 'Reservation Types', endpoint: '/reservation-type', durationLabel: 'Duration (hours)' },
  trainingPackages: { title: 'Training Packages', endpoint: '/training-package', durationLabel: 'Duration (weeks)', packages: true }
};
const emptyDraft = { name: '', duration: '', cost: '', description: '', trainingFrequencyPerWeek: '' };
const cellStyle = { padding: '0.5rem', borderBottom: '1px solid #dee2e6', verticalAlign: 'top' };
const inputStyle = { width: '100%', minWidth: 0, boxSizing: 'border-box', padding: '0.4rem', border: '1px solid #ccc', borderRadius: '4px' };

const OrganizationCatalog = ({ kind }) => {
  const { currentUser, fetchReservationTypes } = useApp();
  const config = catalogs[kind];
  const canEdit = ['admin', 'superuser'].includes(currentUser?.user?.orgRole);
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(emptyDraft);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [version, setVersion] = useState(0);
  const headers = { 'session-id': currentUser?.sessionId };
  useEffect(() => {
    let active = true;
    setItems([]);
    setDraft(emptyDraft);
    setEditing(null);
    setLoaded(false);
    setError('');
    if (!canEdit || !currentUser?.sessionId) return;
    setLoading(true);
    apiClient(`${config.endpoint}/all`, { headers: { 'session-id': currentUser.sessionId } })
      .then(async response => {
        const data = await response.json();
        if (!response.ok) throw new Error(data?.error || `Unable to load ${config.title.toLowerCase()}.`);
        if (!Array.isArray(data)) throw new Error('Invalid catalog response.');
        if (active) { setItems(data); setLoaded(true); }
      })
      .catch(failure => { if (active) setError(failure.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [config.endpoint, config.title, canEdit, currentUser?.sessionId, currentUser?.user?.orgId, version]);
  const fields = [
    { key: 'name', label: 'Name', type: 'text' },
    { key: 'duration', label: config.durationLabel, type: 'number' },
    { key: 'cost', label: 'Cost', type: 'number' },
    ...(config.packages ? [{ key: 'trainingFrequencyPerWeek', label: 'Training frequency per week', type: 'number' }] : []),
    { key: 'description', label: 'Description', type: 'textarea' }
  ];
  const save = async (value, existing = false) => {
    const payload = { name: value.name.trim(), description: value.description || '' };
    if (!payload.name || payload.name.length > 200) { setError('Name must contain 1 to 200 characters.'); return; }
    if (payload.description.length > 5000) { setError('Description must be 5,000 characters or fewer.'); return; }
    for (const key of ['duration', 'cost', ...(config.packages ? ['trainingFrequencyPerWeek'] : [])]) {
      const raw = value[key];
      const number = raw === '' || raw == null ? null : Number(raw);
      if (number != null && (!Number.isFinite(number) || (key === 'cost' ? number < 0 : number < 0.01))) {
        setError(key === 'cost' ? 'Cost must be a nonnegative number or blank.' : config.packages ? 'Duration and training frequency must be at least 0.01 or blank.' : 'Duration must be at least 0.01 hours or blank.');
        return;
      }
      payload[key] = number;
    }
    if (existing) payload.id = value.id;
    setBusy(true);
    setError('');
    try {
      const response = await apiClient(config.endpoint, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const result = await response.json();
      if (!response.ok) throw new Error(result?.error || `Unable to save (HTTP ${response.status}).`);
      if (!config.packages) await fetchReservationTypes?.();
      setVersion(previous => previous + 1);
    } catch (failure) { setError(failure.message); }
    finally { setBusy(false); }
  };
  const remove = async item => {
    if (!window.confirm(`Delete ${item.name}?${config.packages ? '' : ' Existing reservations will be preserved.'}`)) return;
    setBusy(true);
    setError('');
    try {
      const response = await apiClient(`${config.endpoint}/${encodeURIComponent(item.id)}`, { method: 'DELETE', headers });
      if (!response.ok) {
        const result = await response.json();
        throw new Error(result?.error || `Unable to delete (HTTP ${response.status}).`);
      }
      if (!config.packages) await fetchReservationTypes?.();
      setVersion(previous => previous + 1);
    } catch (failure) { setError(failure.message); }
    finally { setBusy(false); }
  };
  const editor = (field, value, update) => {
    const props = { 'aria-label': field.label, value: value[field.key] ?? '', disabled: busy, onChange: event => update({ ...value, [field.key]: event.target.value }), style: inputStyle };
    return field.type === 'textarea' ? <textarea {...props} rows={2} maxLength={5000} /> : <input {...props} type={field.type} required={field.key === 'name'} maxLength={field.key === 'name' ? 200 : undefined} min={field.key === 'cost' ? 0 : 0.01} step="0.01" />;
  };
  if (!canEdit) return null;
  return <section className="management-form">
    <h3>{config.title}</h3>
    {config.packages && <p style={{ color: '#526671', margin: '0 0 1rem' }}>For now, training packages are used by the chatbot to inform site visitors about your programs, if you use the chatbot on your website.</p>}
    {error && <p role="alert" style={{ color: '#b91c1c' }}>{error}</p>}
    {loading && <p role="status">Loading {config.title.toLowerCase()}...</p>}
    {!loading && !loaded && <button type="button" onClick={() => setVersion(previous => previous + 1)}>Retry</button>}
    {loaded && <>
      <div style={{ overflowX: 'auto' }}><table className={config.packages ? 'training-packages-table' : 'reservation-types-table'} style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: config.packages ? '760px' : '640px' }}>
        <colgroup>
          <col style={{ width: '22%' }} />
          <col style={{ width: '104px' }} />
          <col style={{ width: '88px' }} />
          {config.packages && <col style={{ width: '120px' }} />}
          <col />
          <col style={{ width: '84px' }} />
        </colgroup>
        <thead><tr>{fields.map(field => <th key={field.key} style={{ ...cellStyle, textAlign: 'left' }}>{field.label}</th>)}<th className="catalog-actions" style={cellStyle}>Actions</th></tr></thead>
        <tbody>{items.map(item => <tr key={item.id}>
          {fields.map(field => <td key={field.key} className={field.key === 'description' ? 'catalog-description' : undefined} style={cellStyle}>{editing?.id === item.id ? editor(field, editing, setEditing) : item[field.key] ?? ''}</td>)}
          <td className="catalog-actions" style={cellStyle}><div className="catalog-action-buttons">{editing?.id === item.id ? <>
            <ActionIconButton action="save" label={`Save ${item.name}`} disabled={busy} onClick={() => save(editing, true)} />
            <ActionIconButton action="cancel" label={`Cancel editing ${item.name}`} disabled={busy} onClick={() => setEditing(null)} />
          </> : <>
            <ActionIconButton action="edit" label={`Edit ${item.name}`} disabled={busy} onClick={() => { setEditing({ ...emptyDraft, ...item }); setError(''); }} />
            <ActionIconButton action="delete" label={`Delete ${item.name}`} disabled={busy} onClick={() => remove(item)} />
          </>}</div></td>
        </tr>)}</tbody>
      </table></div>
      {!items.length && <p>No {config.title.toLowerCase()}.</p>}
      <form onSubmit={event => { event.preventDefault(); save(draft); }} style={{ marginTop: '1.5rem' }}>
        <h4>Add {config.packages ? 'Training Package' : 'Reservation Type'}</h4>
        <div className={`form-fields ${config.packages ? 'training-packages-form-fields' : 'reservation-types-form-fields'}`}>{fields.map(field => <label key={field.key} className={field.key === 'description' ? 'catalog-description' : undefined}>{field.label}{editor(field, draft, setDraft)}</label>)}</div>
        <button type="submit" className="form-primary" disabled={busy} style={{ marginTop: '0.75rem' }}>{busy ? 'Saving...' : 'Add'}</button>
      </form>
    </>}
  </section>;
};

export default OrganizationCatalog;